import angular from 'angular';
import uiRouter from '@uirouter/angularjs';
import url from 'url';
import { filter } from 'lodash';

import UtilModule from './util';
import ServiceModule from './service';
import DirectiveModule from './directive';
import ComponentModule from './component';

import analytics, { GoogleAnalytics, QuoraPixel } from './analytics';

export * from './state';
export * from './util';

const SUBDOMAIN_LANDING = '';
const SUBDOMAIN_APPLICATION = 'app';
const SUBDOMAIN_LEGAL = 'legal';

analytics.configure([
    new GoogleAnalytics(CONFIGURATION.googleAnalyticsTrackingId, CONFIGURATION.googleAdsTrackingId, getAllDomainsExcept(location.hostname), CONFIGURATION.version),
    new QuoraPixel(CONFIGURATION.quoraPixelTrackingId)
]);

const onErrorCallback = window.onerror;

window.onerror = (errorMessage, url, lineNumber, columnNumber, errorObject) => {
    let error = errorObject
        ? typeof errorObject.stack === 'string'
            ? errorObject.stack.replace(new RegExp(`${window.location.protocol}//${window.location.host}`, 'g'), '')
            : errorObject.stack.toString()
        : `${String.prototype.substr.call(errorMessage || '', 0, 4096)} @ ${url}:${lineNumber}:${columnNumber}`;

    analytics.trackClientError(error);

    if (typeof onErrorCallback === 'function') {
        return onErrorCallback(errorMessage, url, lineNumber, columnNumber, errorObject);
    }

    return false;
};

export default angular
    .module('common', [
        uiRouter,

        UtilModule.name,
        ServiceModule.name,
        DirectiveModule.name,
        ComponentModule.name
    ])
    .config(storageConfiguration)
    .config(provideConfiguration)
    .run(analyticsPageTrackerConfiguration)
    .config(compilerConfiguration)
    .run(externalUrlConfiguration)
    .run(logStateConfiguration)
    .run(serviceConfiguration);

/* @ngInject */
export function serviceConfiguration(apiService, configuration) {
    apiService.api = configuration.api || null;
    apiService.version = configuration.version || null;
}

/* @ngInject */
export function storageConfiguration(storageServiceProvider) {
    storageServiceProvider.setPrefix('dollarbird');
}

/* @ngInject */
export function provideConfiguration($provide, storageServiceProvider) {
    let configuration = angular.merge({}, CONFIGURATION);

    if (CONFIGURABLE) {
        configuration = angular.merge(configuration, storageServiceProvider.getItem('configuration') || {});
    }

    if (VERBOSE) {
        // eslint-disable-next-line no-console
        console.log(`configuration = ${JSON.stringify(configuration, null, 2)}`);
    }

    $provide.constant('configuration', configuration);
}

/* @ngInject */
export function analyticsPageTrackerConfiguration($location, navigationService) {
    navigationService.onSuccess(() => analytics.trackPageView($location.path()));
}

export function getPresentationStateUrl(url = '') {
    return buildUrl(SUBDOMAIN_LANDING, url);
}

export function getApplicationStateUrl(url = '') {
    return buildUrl(SUBDOMAIN_APPLICATION, url);
}

export function getLegalStateUrl(url = '') {
    return buildUrl(SUBDOMAIN_LEGAL, url);
}

function getBaseHostname(hostname) {
    let subdomainSelector = [
        SUBDOMAIN_LANDING,
        SUBDOMAIN_APPLICATION,
        SUBDOMAIN_LEGAL
    ].map((subdomain) => `(^${subdomain}\\.)`).join('|');

    return hostname.replace(new RegExp(subdomainSelector), '');
}

function getAllDomainsExcept(hostname) {
    return filter([
        buildDomain(SUBDOMAIN_LANDING),
        buildDomain(SUBDOMAIN_APPLICATION),
        buildDomain(SUBDOMAIN_LEGAL)
    ], (item) => item !== hostname);
}

function isIpAddress(hostname) {
    return /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
}

function buildUrl(subdomain, url) {
    return `${location.protocol}//${buildDomain(subdomain)}${url || ''}`;
}

function buildDomain(subdomain) {
    let hostname = getBaseHostname(location.hostname);

    if (subdomain && subdomain.length > 0) {
        return `${subdomain}.${hostname}`;
    }

    return hostname;
}

/* @ngInject */
function externalUrlConfiguration($transitions, $state, $location, $window) {
    $transitions.onStart({
        to(state) {
            return state.data && state.data.external;
        }
    }, (transition) => {
        let state = transition.to();
        let options = state.data.external || {};

        let originalUrl = $state.href(state, transition.params());

        let builtUrl = url.parse(originalUrl, true, true);

        // make sure the new host will be calculated from hostname and port
        builtUrl.protocol = options.mailto ? 'mailto' : builtUrl.protocol;
        builtUrl.host = null;
        builtUrl.port = $location.port();

        // Subdomains don't work with IP addresses (e.g. app.192.168.0.120 is not resolvable).
        // Extract the subdomain from the hostname and pass it as a query parameter instead.
        if (builtUrl.hostname) {
            let baseHostname = getBaseHostname(builtUrl.hostname);

            if (isIpAddress(baseHostname)) {
                let subdomain = builtUrl.hostname.replace(baseHostname, '').replace(/\.$/, '');

                if (subdomain) {
                    builtUrl.hostname = baseHostname;
                    builtUrl.query = builtUrl.query || {};
                    builtUrl.query._subdomain = subdomain;
                    builtUrl.search = null;
                }
            }
        }

        $window.open(builtUrl.format(), options.blank ? '_blank' : '_self');

        return false;
    });
}

/* @ngInject */
function logStateConfiguration($state, $log, $transitions) {
    if (!PRODUCTION) {
        // $state.defaultErrorHandler(angular.noop);

        $transitions.onStart({}, (transition) => {
            $log.debug(`Transition started:   ${transition}`);
        }, { priority: 100 });

        $transitions.onSuccess({}, (transition) => {
            $log.debug(`Transition succeeded: ${transition}`);
        }, { priority: 100 });

        $transitions.onError({}, (transition) => {
            $log.debug(`Transition canceled:  ${transition}; Reason: ${transition.error()}`);
        }, { priority: 100 });
    }
}

/* @ngInject */
function compilerConfiguration($compileProvider) {
    if (PRODUCTION) {
        $compileProvider.debugInfoEnabled(false);
        $compileProvider.commentDirectivesEnabled(false);
        $compileProvider.cssClassDirectivesEnabled(false);
    }
}



// WEBPACK FOOTER //
// ./src/common/index.js